/**
 * @file index.tsx
 * @description Root providers component that wraps the application with context providers.
 * Supplies global labels and other shared state to the component tree.
 * Registers client-only components for proper client-side hydration.
 */

'use client';

import React, { useMemo } from 'react';

import { IDictionaryItems, ISiteSettings } from '@/.generated';
import { GlobalLabelsProvider } from '@/context/GlobalLabelContext';
import { SiteSettingsProvider } from '@/context/SiteSettingsContext';
import { LocalizedRoutesProvider } from '@/context/LocalizedRoutesContext';
import { useHashLinkScroll } from '@/lib/hooks/useHashLinkScroll';

// Register client components within the client boundary
import '@/temp/registered-client-only-components';

/**
 * Props interface for Providers component.
 */
interface ProvidersProps {
  /** Child components to wrap with providers */
  children: React.ReactNode;
  /** Data to provide to context consumers */
  data: {
    /** Global labels/translations from CMS */
    globalLabels: IDictionaryItems | object;
    /** Site-wide settings (favicons, SEO defaults, integrations) from CMS */
    siteSettings: ISiteSettings | object;
    /** Prefix-free paths that are actually localized in the active locale */
    localizedUrls: string[];
  };
}

/**
 * Root providers component that wraps the application with context providers.
 * Currently provides global labels context, with the ability to add more providers as needed.
 *
 * This component:
 * - Runs on the client side only ('use client')
 * - Wraps children with GlobalLabelsProvider for translations/dictionary access
 * - Can be extended with additional providers (Theme, Auth, etc.)
 *
 * Used by locale layout to provide context to all pages within a locale.
 *
 * @param {ProvidersProps} props - Component props
 * @returns {JSX.Element} Children wrapped with context providers
 *
 * @example
 * <Providers data={{ globalLabels: labels }}>
 *   <App />
 * </Providers>
 */
export function Providers({ children, data }: ProvidersProps) {
  useHashLinkScroll();
  // Rebuild the Set only when the underlying array reference changes (per navigation).
  const localizedUrls = useMemo(() => new Set(data.localizedUrls), [data.localizedUrls]);
  return (
    <GlobalLabelsProvider value={{ globalLabels: data.globalLabels }}>
      <SiteSettingsProvider value={{ siteSettings: data.siteSettings }}>
        <LocalizedRoutesProvider value={{ localizedUrls }}>{children}</LocalizedRoutesProvider>
      </SiteSettingsProvider>
    </GlobalLabelsProvider>
  );
}
